import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Seo } from "@/components/seo/Seo";
import { useCart } from "@/lib/cart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_POLLS = 12; // ~24s
const INTERVAL = 2000;

export const OrderConfirmation = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState({ status: "polling", payment_status: null, order_number: null, attempts: 0 });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment_status: null, order_number: null, attempts: 0 });
      return;
    }
    let cancelled = false;

    const tick = async (attempt) => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          clear(); // empty the localStorage cart
          setState({ status: "paid", payment_status: "paid", order_number: data.order_number, attempts: attempt });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", payment_status: data.payment_status, order_number: null, attempts: attempt });
          return;
        }
        if (attempt >= MAX_POLLS) {
          setState({ status: "timeout", payment_status: data.payment_status, order_number: null, attempts: attempt });
          return;
        }
        setTimeout(() => tick(attempt + 1), INTERVAL);
      } catch {
        if (cancelled) return;
        setState({ status: "error", payment_status: null, order_number: null, attempts: attempt });
      }
    };
    tick(1);
    return () => { cancelled = true; };
  }, [sessionId, clear]);

  return (
    <>
      <Seo title="Order confirmation · Discount Fillers" path="/order-confirmation" />
      <Header />
      <main className="pt-28 md:pt-32 pb-24 bg-neutral-50 min-h-[60vh]">
        <div className="max-w-[760px] mx-auto px-6">
          <div className="bg-white border border-neutral-200 p-8 md:p-12 text-center" data-testid="order-confirmation">
            {state.status === "polling" && (
              <>
                <Loader2 className="w-12 h-12 text-neutral-400 mx-auto animate-spin" />
                <h1 className="font-heading text-3xl font-black tracking-tight mt-5">Confirming your payment…</h1>
                <p className="text-neutral-600 mt-2 text-sm">This usually takes a few seconds. Do not close this page.</p>
              </>
            )}
            {state.status === "paid" && (
              <>
                <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
                <h1 className="font-heading text-4xl font-black tracking-tight mt-5" data-testid="order-success-title">
                  Payment received 🎉
                </h1>
                <p className="text-neutral-600 mt-2 text-sm">
                  Your order <span className="font-bold text-black">{state.order_number}</span> is confirmed.
                  We are preparing it for DHL Express dispatch within 24h.
                </p>
                <p className="text-xs text-neutral-500 mt-2">
                  A confirmation email with tracking will arrive shortly.
                </p>
                <Link
                  to="/"
                  className="inline-block mt-6 h-12 px-7 bg-black text-white text-sm font-medium inline-flex items-center justify-center"
                  data-testid="order-back-home"
                >
                  Continue shopping
                </Link>
              </>
            )}
            {(state.status === "timeout" || state.status === "expired" || state.status === "error") && (
              <>
                <AlertCircle className="w-12 h-12 text-amber-600 mx-auto" />
                <h1 className="font-heading text-3xl font-black tracking-tight mt-5">
                  {state.status === "expired" ? "Session expired" : "Still confirming"}
                </h1>
                <p className="text-neutral-600 mt-2 text-sm">
                  {state.status === "expired"
                    ? "Your Stripe session expired before payment completed. You can start a new checkout."
                    : "Your payment may still be processing. Check your email — if you have been charged, the order is on its way."}
                </p>
                <Link
                  to="/checkout"
                  className="inline-block mt-6 h-12 px-7 border border-black text-sm font-medium inline-flex items-center justify-center"
                >
                  Back to checkout
                </Link>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};
